import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TopBarHeader from './TopBarHeader';
import TopBarMiddle from './TopBarMiddle';
import TopBarBottom from './TopBarBottom';
import AmplifierAlarmBanner from './AmplifierAlarmBanner';
import RemotePopupModal from './RemotePopupModal';
import './amplifierControl.css';
import {
    AmplifierData,
    AmplifierProjectData,
    ProgramStatusMessage,
    HamlibControlRequestMessage,
    type AuditLogEntry,
    type RemotePopupAction,
    type RemotePopupState,
    type UpdateAlarmLogStatusMessage,
} from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import { createEmptyAmplifierProjectData } from './remoteControlMapper';

interface TopBarProps {
    isConnected: boolean;
    isMcuConnected: boolean;
    onAntennaChange: (mode: 'manual' | 'auto') => void;
    onTriggerModeChange: (mode: 'PTT1' | 'PP2' | '输入功率触发') => void;
    onLanguageChange?: (lang: import('../../i18n/amplifier/amplifierLanguage').AmplifierLocaleId) => void;
    triggerMode?: number;
    /** 嵌入其它页面（如无线电远程覆盖层）时传入，优先于路由 state */
    deviceIdOverride?: string;
    /** remote_control.data.device_id 的十进制校验值（来自 my-session.serialNumber） */
    remoteControlDeviceIdOverride?: string | number | null;
}

export const TopBar: React.FC<TopBarProps> = ({
    isConnected,
    isMcuConnected,
    onAntennaChange,
    onTriggerModeChange,
    onLanguageChange,
    triggerMode,
    deviceIdOverride,
    remoteControlDeviceIdOverride,
}) => {
    const subscriberKeyRef = useRef(`topbar-${Math.random().toString(36).slice(2)}`);
    const location = useLocation();
    const locationState = location.state as any;
    const deviceId =
        (deviceIdOverride != null && String(deviceIdOverride).trim() !== '')
            ? String(deviceIdOverride).trim()
            : (locationState?.deviceId || 'fallback-amplifier-device');
    const remoteControlDeviceId =
        remoteControlDeviceIdOverride ??
        locationState?.remoteControlDeviceId ??
        locationState?.serialNumber ??
        null;
    const source = locationState?.source || 'unknown';
    const timestamp = locationState?.timestamp || Date.now();

    // 确保deviceId存在，如果不存在则使用默认值或提示用户
    useEffect(() => {
        console.log('Index组件 - 进入页面，执行设备ID检查');
        console.log('Index组件 - location.state:', locationState);
        console.log('Index组件 - 获取到的设备ID:', deviceId);
        console.log('Index组件 - 设备ID来源:', source);
        console.log('Index组件 - 时间戳:', timestamp);
        console.log('Index组件 - location:', location);
        if (!deviceId) {
            console.warn('设备ID获取失败，可能导致部分功能无法正常工作');
            // 可以在这里添加用户提示，或者使用默认设备ID
        }
    }, [locationState]);

    // 组件挂载时执行
    useEffect(() => {
        console.log('Index组件 - 组件挂载');
    }, []);

    // 确保组件渲染
    console.log('Index组件 - 渲染中，设备ID:', deviceId);


    const [isAntenna1On, setIsAntenna1On] = useState(false);
    const [isAntenna2On, setIsAntenna2On] = useState(false);
    const [triggerModeNumber, setTriggerModeNumber] = useState<number>(0);
    const [amplifierData, setAmplifierData] = useState<AmplifierData | null>(null);
    const [antennaMode, setAntennaMode] = useState<'manual' | 'auto'>('manual');
    const [programStatusMessage, setProgramStatusMessage] = useState<ProgramStatusMessage | null>(null);
    const [hamlibControlRequestMessage, setHamlibControlRequestMessage] = useState<HamlibControlRequestMessage | null>(null);
    const [auditLogEntries, setAuditLogEntries] = useState<AuditLogEntry[]>([]);
    const [isAlarmInfoModalVisible, setIsAlarmInfoModalVisible] = useState(false);
    const [remotePopup, setRemotePopup] = useState<RemotePopupState>({ open: false, message: '' });
    const [isRemotePopupActionPending, setIsRemotePopupActionPending] = useState(false);
    const [amplifierProjectData, setAmplifierProjectData] = useState<AmplifierProjectData>(
        createEmptyAmplifierProjectData,
    );

    const remotePopupRef = useRef(remotePopup);

    useEffect(() => {
        remotePopupRef.current = remotePopup;
    }, [remotePopup]);

    // 订阅设备状态，获取 AmplifierData 数据并转换为 AmplifierProjectData，同时获取程序状态消息和Hamlib控制指令
    useEffect(() => {
        console.log('Index组件 - 开始订阅设备状态');
        const subscriberKey = subscriberKeyRef.current;
        setRemotePopup({ open: false, message: '' });
        setIsRemotePopupActionPending(false);

        // 订阅设备状态（开发环境 MQTT 未就绪时静默跳过，避免预览卡片报错）
        void amplifierMqttService.subscribeToDeviceStatus(
            deviceId,
            (data: AmplifierData) => {
                // 保存完整的 AmplifierData
                setAmplifierData(data);

                if (data._source === 'remote_control') {
                    return;
                }

                // 转换 AmplifierData 为 AmplifierProjectData
                const projectData: AmplifierProjectData = {
                    voltageCalibrationCoefficient: data.voltageCalibrationCoefficient,
                    currentCalibrationCoefficient: data.currentCalibrationCoefficient,
                    frequencyMeasurementCalibrationCoefficient: data.frequencyMeasurementCalibrationCoefficient,
                    vgsCalibrationCoefficient: 0,
                    exdacRefvCalibrationMv: 0,
                    protectionVoltage: data.protectionVoltage,
                    protectionCurrent: data.protectionCurrent,
                    protectionTemperature: data.protectionTemperature,
                    protectionInputPowerPositive: data.protectionInputPowerPositive,
                    protectionInputPowerNegative: data.protectionInputPowerNegative,
                    protectionOutputPowerPositive: data.protectionOutputPowerPositive,
                    protectionOutputPowerNegative: data.protectionOutputPowerNegative,
                    protectionRf: data.protectionRf,
                    protectionInSwr: data.protectionRf,
                    protectionOutSwr: data.protectionRf,
                    protectionFrequencyError: data.protectionFrequencyError,
                    rfTuneValue: data.rfTuneValue,
                    powerTriggerPttVoltage: data.powerTriggerPttVoltage,
                    protectMask: 0,
                    inputPowerPositiveCompareVoltage: data.inputPowerPositiveCompareVoltage,
                    inputPowerNegativeCompareVoltage: data.inputPowerNegativeCompareVoltage,
                    powerInputPowerPositiveCompareVoltage: data.powerInputPowerPositiveCompareVoltage,
                    powerOutputPowerPositiveCompareVoltage: data.powerOutputPowerPositiveCompareVoltage,
                    outputPowerPositiveCompareVoltage: data.outputPowerPositiveCompareVoltage,
                    outputPowerNegativeCompareVoltage: data.outputPowerNegativeCompareVoltage,
                    overCurrentCompareVoltage: data.overCurrentCompareVoltage,
                    bandParam: data.bandParam,
                    currentBandNo: 0,
                };

                console.log('Index组件 - 转换后的工程参数数据:', projectData);
                setAmplifierProjectData(projectData);
            },
            (programStatusMessage: ProgramStatusMessage) => {

                setProgramStatusMessage(programStatusMessage);
            },
            (hamlibControlRequestMessage: HamlibControlRequestMessage) => {

                setHamlibControlRequestMessage(hamlibControlRequestMessage);
                console.log('Index组件 - 接收到Hamlib控制指令:', hamlibControlRequestMessage);
            },
            (auditLogMessage: UpdateAlarmLogStatusMessage) => {
                const list = Array.isArray(auditLogMessage.data?.list) ? auditLogMessage.data.list : [];
                setAuditLogEntries(list);
            },
            (projectData: AmplifierProjectData) => {
                console.log('Index组件 - 接收到remote_control工程参数:', projectData);
                setAmplifierProjectData(projectData);
            },
            (popupState: RemotePopupState) => {
                setRemotePopup(popupState);
                if (!popupState.open) {
                    setIsRemotePopupActionPending(false);
                }
            },
            subscriberKey,
            remoteControlDeviceId
        );

        // 组件卸载时取消订阅
        return () => {
            console.log('Index组件 - 取消订阅设备状态');
            amplifierMqttService.unsubscribeFromDeviceStatus(deviceId, subscriberKey);
        };
    }, [deviceId, remoteControlDeviceId]);

    useEffect(() => {
        if (triggerMode !== undefined) {
            const modeMap: { [key: number]: number } = {
                0: 0,
                1: 1,
                2: 2,
                3: 3
            };
            setTriggerModeNumber(modeMap[triggerMode] || 0);
        }
    }, [triggerMode]);

    // 触发模式以设备为准：从 MQTT 的 amplifierData 同步，不下发时不做乐观更新
    useEffect(() => {
        if (amplifierData?.triggerMode !== undefined) {
            setTriggerModeNumber(amplifierData.triggerMode);
        }
    }, [amplifierData?.triggerMode]);

    const handleTriggerModeChange = (mode: 'PTT1' | 'PP2' | '输入功率触发') => {
        onTriggerModeChange(mode);
    };

    const handleAntennaChange = (mode: 'manual' | 'auto') => {
        setAntennaMode(mode);
        onAntennaChange(mode);
    };

    const handleAntenna1Change = (isOn: boolean) => {
        setIsAntenna1On(isOn);
        if (isOn) {
            setIsAntenna2On(false);
            // 移除输入天线对触发模式的自动设置
        }
    };

    const handleAntenna2Change = (isOn: boolean) => {
        setIsAntenna2On(isOn);
        if (isOn) {
            setIsAntenna1On(false);
            // 移除输入天线对触发模式的自动设置
        }
    };

    const alarmWord = amplifierData?.alarmWord ?? 0;

    const handleRemotePopupAction = async (action: RemotePopupAction) => {
        if (isRemotePopupActionPending) return;

        const previousPopup = remotePopupRef.current;
        if (previousPopup.popupType === 'update_progress') return;

        const popupId = previousPopup.popupId?.trim();
        const popupType = previousPopup.popupType ?? 'pa_switch';
        if (!popupId) {
            console.warn('Index组件 - remote_popup 缺少 popup_id，无法回传 action');
            return;
        }

        setRemotePopup((current) => ({ ...current, open: false }));
        setIsRemotePopupActionPending(true);
        try {
            await amplifierMqttService.sendRemotePopupAction(deviceId, action, {
                popupId,
                popupType,
            });
        } catch (error) {
            console.error('Index组件 - remote_popup action 发送失败:', error);
            setRemotePopup(previousPopup);
            setIsRemotePopupActionPending(false);
        }
    };

    const isRestartPopupOpen =
        remotePopup.open && remotePopup.popupType === 'restart';

    return (
        <div className="amplifier-control">
            <TopBarHeader
                isConnected={isConnected}
                isMcuConnected={isMcuConnected}
                onAntennaChange={handleAntennaChange}
                onTriggerModeChange={handleTriggerModeChange}
                onLanguageChange={onLanguageChange}
                triggerMode={triggerModeNumber}
                amplifierProjectData={amplifierProjectData}
                deviceId={deviceId}
                amplifierData={amplifierData}
                programStatusMessage={programStatusMessage}
                hamlibControlRequestMessage={hamlibControlRequestMessage}
                auditLogEntries={auditLogEntries}
                alarmInfoModalOpen={isAlarmInfoModalVisible}
                onAlarmInfoModalOpenChange={setIsAlarmInfoModalVisible}
                isRestartPopupOpen={isRestartPopupOpen}
            />
            <TopBarMiddle
                deviceId={deviceId}
                amplifierData={amplifierData}
                programStatusMessage={programStatusMessage}
                hamlibControlRequestMessage={hamlibControlRequestMessage}
            />
            <TopBarBottom
                deviceId={deviceId}
                amplifierData={amplifierData}
                antennaMode={antennaMode}
            />
            {alarmWord !== 0 && (
                <AmplifierAlarmBanner
                    alarmWord={alarmWord}
                    onViewDetails={() => setIsAlarmInfoModalVisible(true)}
                />
            )}
            <RemotePopupModal
                open={remotePopup.open}
                message={remotePopup.message}
                popupType={remotePopup.popupType}
                pending={isRemotePopupActionPending}
                onConfirm={() => void handleRemotePopupAction('confirm')}
                onCancel={() => void handleRemotePopupAction('cancel')}
            />
        </div>
    );
};

export default TopBar;